import type { FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import Image from 'next/image'
import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { pad } from '@/lib/format'

interface ProjectCardProps {
    project: ProjectSummary
    index: number
    aspect?: string
    tone?: 'light' | 'dark'
    sizes?: string
    large?: boolean
    className?: string
}

const ProjectCard: FC<ProjectCardProps> = ({
    project,
    index,
    aspect = 'aspect-[4/3]',
    tone = 'dark',
    sizes = '(min-width: 768px) 50vw, 100vw',
    large = false,
    className
}): ReactNode => {
    const dark = tone === 'dark'

    return (
        <article data-reveal className={cn('group', className)}>
            <Link
                href={`/work/${project.slug}`}
                className='block rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-coral'
            >
                <div data-reveal-image className={cn('relative overflow-hidden', dark ? 'bg-charcoal-soft' : 'bg-stone', aspect)}>
                    {project.cover && (
                        <Image
                            src={project.cover}
                            alt={project.coverAlt}
                            fill
                            sizes={sizes}
                            className='object-cover transition-[scale] duration-[1400ms] ease-[var(--ease-out-expo)] group-hover:scale-[1.04]'
                        />
                    )}

                    <span aria-hidden='true' className='absolute inset-0 bg-charcoal/0 transition-colors duration-700 group-hover:bg-charcoal/15' />

                    <span
                        aria-hidden='true'
                        className='absolute inset-x-0 bottom-0 h-1.5 origin-left scale-x-0 transition-transform duration-700 ease-[var(--ease-out-expo)] group-hover:scale-x-100'
                        style={{ backgroundColor: project.accent }}
                    />

                    <span className='absolute left-3 top-3 flex flex-wrap gap-2 md:left-4 md:top-4'>
                        {project.category && (
                            <span className='rounded-sm bg-cream px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-charcoal'>
                                {project.category}
                            </span>
                        )}
                        {project.isConcept && (
                            <span className='rounded-sm bg-charcoal px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.16em] text-cream'>
                                Concept
                            </span>
                        )}
                    </span>

                    <span className='absolute right-3 top-3 rounded-sm bg-charcoal/80 px-2 py-1 font-mono text-[10px] text-cream md:right-4 md:top-4'>
                        / {pad(index + 1)}
                    </span>

                    <span className='absolute bottom-4 right-4 inline-flex h-11 items-center gap-2 rounded-sm bg-coral px-3.5 text-[12px] font-semibold uppercase tracking-[0.1em] text-charcoal transition-[opacity,translate] duration-500 ease-[var(--ease-out-expo)] md:translate-y-3 md:opacity-0 md:group-hover:translate-y-0 md:group-hover:opacity-100 md:group-focus-visible:translate-y-0 md:group-focus-visible:opacity-100'>
                        <span className='hidden md:inline'>View case study</span>
                        <ArrowUpRight aria-hidden='true' className='size-5 transition-transform duration-500 group-hover:rotate-45' />
                    </span>
                </div>

                <div className='mt-5 grid gap-4 sm:grid-cols-[1fr_auto] sm:gap-8'>
                    <div>
                        <h3
                            className={cn(
                                'flex items-center font-heading font-wide font-black uppercase leading-[0.9] tracking-[-0.02em]',
                                large ? 'text-[clamp(1.85rem,3.2vw,3rem)]' : 'text-[clamp(1.5rem,2.2vw,2.1rem)]'
                            )}
                        >
                            <span aria-hidden='true' className='h-[0.12em] w-0 shrink-0 bg-coral transition-[width,margin] duration-500 ease-[var(--ease-out-expo)] group-hover:mr-[0.3em] group-hover:w-[0.6em] motion-reduce:transition-none' />
                            {project.name}
                        </h3>
                        <p className={cn('mt-3 max-w-md text-[15px] leading-relaxed', dark ? 'text-cream/80' : 'text-charcoal/80')}>
                            {project.summary}
                        </p>
                    </div>

                    <dl className={cn('grid content-start gap-3 text-[13px] sm:max-w-[13rem] sm:text-right', dark ? 'text-cream/85' : 'text-charcoal/85')}>
                        <div>
                            <dt className={cn('font-mono text-[10px] uppercase tracking-[0.18em]', dark ? 'text-cream/60' : 'text-charcoal/70')}>Industry</dt>
                            <dd className='mt-1'>{project.industry}</dd>
                        </div>
                        {project.services.length > 0 && (
                            <div>
                                <dt className={cn('font-mono text-[10px] uppercase tracking-[0.18em]', dark ? 'text-cream/60' : 'text-charcoal/70')}>Services</dt>
                                <dd className='mt-1 leading-snug'>{project.services.map((service) => service.title).join(', ')}</dd>
                            </div>
                        )}
                    </dl>
                </div>
            </Link>
        </article>
    )
}

export default ProjectCard
